// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import DataTable from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import loadShipmentPkgsFilesInfo from "@tps/App/Handlers/OrderDetails/LoadShipmentPkgsFilesInfo";
import { CustomAppState } from "@tps/App/Store/Reducers";
import { DataTableProps } from "@tps/MobiusOverrides/DataTable/DataTable";
import { sharedDateTimeOptions } from "@tps/XDL/constants";
import parse from "html-react-parser";
import * as React from "react";
import { useContext, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

// XNG-Changes: call api to get isMtrExist & isPackagingListExist
const mapStateToProps = (state: CustomAppState) => {
    const orderNumber = state.pages.orderDetails.orderNumber || "";
    return {
        pkgInfo: state.xOrderDetails?.[orderNumber]?.shipmentPackages,
        isLoading: state.xOrderDetails?.[orderNumber]?.isLoading,
    };
};

const mapDispatchToProps = {
    loadShipmentPkgsFilesInfo,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OrderDetailsShipmentPackagesStyles {
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    shipmentDateHeader?: DataTableHeaderProps;
    carrierHeader?: DataTableHeaderProps;
    shipViaHeader?: DataTableHeaderProps;
    trackingNumberHeader?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    shipmentDateCell?: DataTableCellProps;
    carrierCell?: DataTableCellProps;
    shipViaCell?: DataTableCellProps;
    trackingNumberCell?: DataTableCellProps;
    // XNG-Changes
    linkToMTR?: LinkPresentationProps;
    mtrCell?: DataTableCellProps;
    packingListLink?: LinkPresentationProps;
    packingListCell?: DataTableCellProps;
}

export const shipmentPackagesStyles: OrderDetailsShipmentPackagesStyles = {};

const styles = shipmentPackagesStyles;

const OrderDetailsShipmentPackages = ({ isLoading, pkgInfo, loadShipmentPkgsFilesInfo }: Props) => {
    const { value: order } = useContext(OrderStateContext);
    if (!order) {
        return null;
    }

    if (!order.shipmentPackages || order.shipmentPackages.length === 0) {
        return null;
    }

    useEffect(() => {
        if (!pkgInfo && !isLoading && order?.shipmentPackages) {
            const packagesNumbers = order.shipmentPackages?.map(pkg => pkg.packageNumber);
            loadShipmentPkgsFilesInfo({ orderNumber: order.erpOrderNumber, ppsNo: packagesNumbers });
        }
    }, []);

    // XNG-Changes
    const formatDate = (dateVlaue: Date) => {
        if (!dateVlaue) {
            return null;
        }

        const date = new Date(dateVlaue);

        // check if it's the date = 1-1-1990
        if (
            date.getFullYear() === 1990 &&
            date.getMonth() === 0 && // January is 0
            date.getDate() === 1
        ) {
            return null;
        }

        return (
            <LocalizedDateTime
                dateTime={date}
                // XNG-Changes:Date Format fix
                options={sharedDateTimeOptions}
            />
        );
    };

    return (
        <DataTable {...styles.dataTable}>
            <DataTableHead {...styles.dataTableHead}>
                <DataTableHeader {...styles.shipmentDateHeader}>{translate("Ship Date")}</DataTableHeader>
                <DataTableHeader {...styles.carrierHeader}>{translate("Carrier")}</DataTableHeader>
                <DataTableHeader {...styles.shipViaHeader}>{translate("Service")}</DataTableHeader>
                <DataTableHeader {...styles.trackingNumberHeader}>{translate("Tracking #")}</DataTableHeader>
                {/* XNG-Changes:  Shipment # */}
                <DataTableHeader {...styles.trackingNumberHeader}>{translate("Shipment #")}</DataTableHeader>
                <DataTableHeader {...styles.trackingNumberHeader}>{translate("MTR")}</DataTableHeader>
                <DataTableHeader {...styles.trackingNumberHeader}>{translate("Packing List")}</DataTableHeader>
            </DataTableHead>
            <DataTableBody {...styles.dataTableBody}>
                {order.shipmentPackages.map(shipmentPackage => (
                    <DataTableRow {...styles.dataTableRow} key={shipmentPackage.id}>
                        <DataTableCell {...styles.shipmentDateCell}>
                            {/* XNG-Changes */}
                            {formatDate(shipmentPackage.shipmentDate)}
                        </DataTableCell>
                        <DataTableCell {...styles.carrierCell}>{shipmentPackage.carrier}</DataTableCell>
                        <DataTableCell {...styles.shipViaCell}>{shipmentPackage.shipVia}</DataTableCell>
                        <DataTableCell {...styles.trackingNumberCell} data-test-selector="orderDetails_TrackingNumber">
                            {parse(shipmentPackage.trackingUrl, parserOptions)}
                        </DataTableCell>
                        <DataTableCell {...styles.trackingNumberCell} data-test-selector="orderDetails_ShipmentNumber">
                            {shipmentPackage.packageNumber}
                        </DataTableCell>
                        {/* XNG-Changes */}
                        <DataTableCell {...styles.mtrCell} data-test-selector="orderDetails_MTR">
                            {pkgInfo?.[shipmentPackage.packageNumber]?.isMtrExist && (
                                <Link
                                    href={`/api/tps/files/mtr/Generate/${
                                        order.erpOrderNumber || order.webOrderNumber
                                    }/${shipmentPackage.packageNumber}`}
                                    {...styles.linkToMTR}
                                    target="_blank"
                                >
                                    {translate("MTR Link")}
                                </Link>
                            )}
                        </DataTableCell>
                        <DataTableCell {...styles.packingListCell} data-test-selector="orderDetails_PackingList">
                            {pkgInfo?.[shipmentPackage.packageNumber]?.isPackingListExist && (
                                <Link
                                    href={`/api/tps/files/packingList/Generate/${
                                        order.erpOrderNumber || order.webOrderNumber
                                    }/${shipmentPackage.packageNumber}`}
                                    {...styles.packingListLink}
                                    target="_blank"
                                >
                                    {translate("Packing List Link")}
                                </Link>
                            )}
                        </DataTableCell>
                    </DataTableRow>
                ))}
            </DataTableBody>
        </DataTable>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OrderDetailsShipmentPackages),
    definition: {
        allowedContexts: ["OrderDetailsPage", "VmiOrderDetailsPage"],
        group: "Order Details",
    },
};

export default widgetModule;
